import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Common from 'src/components/Common';
import { trackScreenView } from 'src/utils/analytics/GAtracker';
import useTheme from 'src/hooks/useTheme';
import { nameToInitials } from 'src/utils';
import routeNames from 'src/constants/routeNames';
import CompanyImage from 'src/components/CompanyImage';
import navigationService from 'src/utils/navigation';
import useProfile from 'src/hooks/useProfile';
import XButton from 'src/components/XButton';
import { useSafeArea } from 'react-native-safe-area-context';
import ConnectionIndicator from 'src/components/ConnectionIndicator';
import Spinner from 'src/components/Common/Spinner';
import { useTwilioClient } from '@chatgenie/chat-utils';
import styles from './style';
import { ServicesList } from './components/ServicesList';
import { LIVE_CHAT_STATES, NEW_REQUEST_STATES } from 'src/config';
import { screenCommonStyles } from 'src/screens/common.styles';
import { NewChatButton } from 'src/components/ScreenButtons/NewChatButton';
import { NewRequestButton } from 'src/components/ScreenButtons/NewRequestButton';
import { withApolloProvider } from 'src/graphql/client-provider';
import {
  useMyThreadAddedSubscription,
  useMyThreadUpdatedSubscription,
  useMyThreadRemovedSubscription,
} from '@chatgenie/thread-service-utils';
import { isThreadServiceEnabled } from 'src/graphql/thread-service.helpers';
import {
  ON_CONTACT_THREAD_ADDED,
  ON_CONTACT_THREAD_REMOVED,
  ON_CONTACT_THREAD_UPDATED,
} from 'src/graphql/subscriptions';
import useChatState from 'src/screens/Chat/hooks/useChatState';
import { normalizeTicket } from 'src/utils/normalizer';
import { useDispatchMapper } from 'src/hooks/actionHooks';
import { setActiveTicketAction } from 'src/redux/chat/chatActions';

const Now = ({ navigation }) => {
  const { userInfo, companyInfo } = useProfile();
  const { theme, isDarkMode } = useTheme();
  const { client } = useTwilioClient();
  const insets = useSafeArea();
  const saveActiveTicket = useDispatchMapper(setActiveTicketAction);
  const { chatState } = useChatState();

  useEffect(() => {
    trackScreenView(routeNames.NOW);
  }, []);

  const handleThreadUpdatedEvent = useCallback((thread) => {
    const normalizedThread = normalizeTicket(thread);
    const hasCurrentThreadUpdated = normalizedThread?.id === chatState.activeTicket?.id;
    if (hasCurrentThreadUpdated) {
      saveActiveTicket(normalizedThread);
    }
  }, [chatState, saveActiveTicket]);

  useMyThreadAddedSubscription(ON_CONTACT_THREAD_ADDED, isThreadServiceEnabled());
  useMyThreadUpdatedSubscription(ON_CONTACT_THREAD_UPDATED, isThreadServiceEnabled(), handleThreadUpdatedEvent);
  useMyThreadRemovedSubscription(ON_CONTACT_THREAD_REMOVED, isThreadServiceEnabled());

  useLayoutEffect(() => {
    navigation.setOptions({
      gestureEnabled: false,
      headerTitle: () => <CompanyImage />,
      headerLeft: () => (
        <Common.TouchableOpacity
          style={styles.accountBtn}
          activeOpacity={0.8}
          onPress={() => navigationService.navigate(routeNames.ACCOUNT)}
        >
          <Common.Text color="#7980AA" size={16}>
            {nameToInitials(userInfo.fullname || userInfo.email)}
          </Common.Text>
        </Common.TouchableOpacity>
      ),
      headerRight: () => <XButton />,
      headerTitleContainerStyle: {
        ...screenCommonStyles.headerTitleContainerStyle,
        width: '70%',
      },
    });
  }, [theme, userInfo]);

  // "all", "live", "request"
  const supportType = companyInfo.supportType || 'live';
  const isLoading = !client;
  const showNewRequestButton = NEW_REQUEST_STATES.includes(supportType);
  const showLiveChatButton = LIVE_CHAT_STATES.includes(supportType);

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Common.View flexGrow={1} style={{ backgroundColor: theme.background }}>
      <ConnectionIndicator />
      <ServicesList />
      <Common.View
        style={[styles.bottomMenu, { height: insets.bottom + 56 }, isDarkMode && { backgroundColor: theme.foreground }]}
      >
        {showNewRequestButton && (
          <NewRequestButton
            btnStyle={[
              styles.actionBtn,
              styles.actionBtnBorder,
              { paddingBottom: insets.bottom },
              isDarkMode && { borderRightColor: theme.background },
            ]}
            labelStyle={styles.btnLabel}
          />
        )}
        {showLiveChatButton && (
          <NewChatButton btnStyle={[styles.actionBtn, { paddingBottom: insets.bottom }]} labelStyle={styles.btnLabel} />
        )}
      </Common.View>
    </Common.View>
  );
};

Now.propTypes = {
  navigation: PropTypes.object.isRequired,
};

export default withApolloProvider(React.memo(Now));
